import { Tab as HeadlessTab } from "@headlessui/react";
import React, { useMemo } from "react";
import { useLocation } from "react-router-dom";

import { BasicTooltip } from "../Tooltip";

import { DynamicTabs } from "./DynamicTabs";
import { TabPanel } from "./Panel";
import { styles } from "./styles";
import { Tab } from "./Tab";
import { TabItem, TabItem as TabItemType, TabNavigationProps } from "./types";
import { getCurrentTabPanelInfo, getSelectedIndex } from "./utils";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { QuickLink } from "@/components/ui/TabNavigation/QuickLinks";
import { cn } from "@/lib/utils";

const TabNavigation = <T extends TabItemType>({
  handleTabChange,
  tabs,
  selectedIndex,
  actionButtons,
  dynamicTabs,
  className,
  onDynamicTabsReorder,
  vertical,
  isUrlBased = false,
  headerComponent,
  shouldBeSticky = true,
  panelClassName,
  quickLinks,
  tabContainerClassName,
  panelContainerClassName,
}: TabNavigationProps<T>) => {
  const location = useLocation();

  const selectedIndexWithLocation = useMemo(
    () => getSelectedIndex(tabs, dynamicTabs, location.pathname),
    [tabs, dynamicTabs, location.pathname],
  );

  const currentTabPanelInfoWithLocation = useMemo(
    () => getCurrentTabPanelInfo(tabs, dynamicTabs, location.pathname),
    [tabs, dynamicTabs, location.pathname],
  );

  const currentSelectedIndex = useMemo(
    () => (selectedIndex !== undefined ? selectedIndex : selectedIndexWithLocation),
    [selectedIndex, selectedIndexWithLocation],
  );

  const allTabs = useMemo(() => [...tabs, ...(dynamicTabs || [])], [tabs, dynamicTabs]);

  const containerClassName = `flex h-full w-full ${!vertical ? "flex-col" : ""}`;

  const tabListClassName = `top-0 z-10 my-1 flex min-w-0 w-full justify-between border-gray-200 bg-white/50 backdrop-blur-sm ${
    !vertical ? "items-center border-b" : "mr-10 border-r pr-2 overflow-y-hidden"
  } ${className}`;

  const tabContainerClassNames = `flex space-x-1 ${tabContainerClassName} ${
    vertical ? "flex-col overflow-y-auto" : ""
  }`;

  const groupedTabs = useMemo(() => {
    const hasGroup = tabs.every(tab => tab.group);

    if (!hasGroup) {
      return [["", tabs] as [string, TabItem[]]];
    }

    return Object.entries(
      tabs.reduce(
        (acc, tab) => {
          const { group } = tab;
          if (group) {
            acc[group] = acc[group] || [];
            acc[group].push(tab);
          }
          return acc;
        },
        {} as Record<string, TabItem[]>,
      ),
    );
  }, [tabs]);

  return (
    <HeadlessTab.Group selectedIndex={currentSelectedIndex}>
      <div className={containerClassName}>
        <HeadlessTab.List className={cn(tabListClassName, { sticky: shouldBeSticky })}>
          <div className="flex shrink-0 flex-col justify-between">
            <div className={tabContainerClassNames}>
              {groupedTabs.map(([group, groupTabs]) => (
                <React.Fragment key={group || "default"}>
                  {group && <div className="my-2 text-sm font-medium text-gray-900">{group}</div>}
                  {groupTabs.map(tab => {
                    const tabIndexInAllTabs = allTabs.findIndex(t => t.id === tab.id);
                    const isActive = currentSelectedIndex === tabIndexInAllTabs;

                    return (
                      <BasicTooltip
                        key={tab.id}
                        content={tab.tooltipContent || undefined}
                        shouldShow={Boolean(tab.tooltipContent)}
                      >
                        <span>
                          <Tab
                            tab={tab}
                            isActive={isActive}
                            vertical={vertical}
                            handleTabChange={handleTabChange}
                          />
                        </span>
                      </BasicTooltip>
                    );
                  })}
                </React.Fragment>
              ))}
              {!!dynamicTabs?.length && dynamicTabs.length > 0 && (
                <div className="!mx-1 min-h-full min-w-[1px] bg-gray-200" />
              )}
            </div>
            {quickLinks && vertical && (
              <div className="flex flex-col gap-2">
                <Accordion className="!ml-0 flex flex-col" type="single" collapsible>
                  <AccordionItem value="quick-links" className="!border-none">
                    <AccordionTrigger className="!p-0 !pt-2 !text-gray-600 transition-colors duration-200 hover:!text-gray-900 hover:!no-underline">
                      <div className="flex items-center">Quick Links</div>
                    </AccordionTrigger>
                    <AccordionContent className="!p-0">
                      {quickLinks.map(link => (
                        <QuickLink key={link.name} {...link} />
                      ))}
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
                <div className="flex items-center">
                  {actionButtons && (
                    <div className="flex items-center space-x-2">{actionButtons}</div>
                  )}
                </div>
              </div>
            )}
          </div>
          <DynamicTabs
            dynamicTabs={dynamicTabs}
            onReorder={onDynamicTabsReorder}
            handleTabChange={handleTabChange}
            currentIndex={currentSelectedIndex}
            staticTabsCount={tabs.length}
          />
          {!vertical && actionButtons && (
            <div className="ml-2 flex min-w-0 flex-1 items-center justify-end">{actionButtons}</div>
          )}
        </HeadlessTab.List>
        <HeadlessTab.Panels className={cn(styles.panels.container, panelContainerClassName)}>
          {allTabs.map(tab => {
            const { title, description } = currentTabPanelInfoWithLocation;
            return (
              <TabPanel<T>
                key={tab.id}
                tab={tab}
                isUrlBased={isUrlBased}
                panelClassName={panelClassName}
                headerComponent={headerComponent}
                title={title || ""}
                description={description || ""}
              />
            );
          })}
        </HeadlessTab.Panels>
      </div>
    </HeadlessTab.Group>
  );
};

export default TabNavigation;
